import { Button, Empty, Spin } from 'antd';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'underscore';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import {
  ADVANCED_ANALYTICS,
  IBestDataset,
  IPlotVariable,
} from '../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { ValidationTabsEnum } from '../../../../types/validation';
import { DT0Format } from '../../../../utils/StaticVariable';
import { AnalyticsSection } from '../../Analytics/components/Layout/AnalyticsSection';
import styles from '../Style/AnalyticsPanel.module.scss';
import { ChartBox } from '../component/ChartBox';
import { AltimeterComparisonForm } from '../component/Forms/AltimeterComparisonForm';

type AltimeterComparisonProps = {
  datasets: IBestDataset[];
  enrichedMetadata: IEnrichedMetadata[];
  previewDatasetId: string;
  previewDatasetTimeRange?: [moment.Moment, moment.Moment];
  coordinates: number[];
  altimeterComparisonImage: string;
  error: string;
  activePanel: string;
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  pointName: string;
  datasetChange: (item: string) => void;
  isUpdating: boolean;
  userInput: IAnalytcisUserInput;
  updateUserInput: (update: Partial<IAnalytcisUserInput>) => void;
};

const limitationMessage =
  'Altimeter comparison is only available for wave height (Hm0) or wind speed (WS10)';
const qualityDefinitionLabel = 'Definition of quality indices';
const qualityDefinitionLink =
  'https://www.metocean-on-demand.com/fileshare/getfile.ashx?type=image&file_name=Definition_Of_Quality_Indices.PNG' as const;

export default function AltimeterComparison(props: AltimeterComparisonProps) {
  const {
    datasets,
    previewDatasetId,
    previewDatasetTimeRange,
    coordinates,
    enrichedMetadata,
    onUpdate,
    error,
    altimeterComparisonImage,
    pointName,
    activePanel,
    datasetChange,
    isUpdating,
    userInput,
    updateUserInput,
  } = props;

  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.AltimeterComparison.variable,
  );
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [notSupported, setNotSupported] = useState(false);
  const [startDate, setStartDate] = useState(
    userInput.AltimeterComparison.startDate ||
      moment(previewDatasetTimeRange?.[0]),
  );
  const [endDate, setEndDate] = useState(
    userInput.AltimeterComparison.endDate ||
      moment(previewDatasetTimeRange?.[1]),
  );

  useEffect(() => {
    updateUserInput({
      AltimeterComparison: {
        dataset: previewDatasetId,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [previewDatasetId, variableX, startDate, endDate]);

  const getAltimeterComparisonImage = useCallback(
    (indexX: IPlotVariable, orderId: string) => {
      const [longitude, latitude] = coordinates;

      if (!indexX || !startDate || !endDate) return;

      const payload: IAdvancedAnalyticsApiPayload = {
        PointName: pointName,
        DatasetId: previewDatasetId,
        OrderId: orderId,
        OriginalOrderId: orderId,
        Longitude: longitude,
        Latitude: latitude,
        StartDate: startDate.format(DT0Format),
        EndDate: endDate.format(DT0Format),
        Title: indexX.Name,
        AcronymX: indexX?.ShortName ?? indexX.Name,
        ColumnIndexX: indexX.Index,
        IntervalCountX: 16,
        AnalyticType: ADVANCED_ANALYTICS.ALTIMETER_COMPARISON,
      };

      onUpdate(payload);
    },
    [coordinates, onUpdate, previewDatasetId, pointName, startDate, endDate],
  );

  useEffect(() => {
    if (
      isEmpty(coordinates) ||
      activePanel !== ValidationTabsEnum.SATELLITE_TAB ||
      !enrichedMetadata
    )
      return;
    const task = enrichedMetadata.find(
      (task: IEnrichedMetadata) =>
        task.DatasetId === previewDatasetId &&
        task.CoordinatesString === coordinates.join(),
    );

    if (!task) return;

    const itemsInfos = task.ItemsInfo.filter(
      (itemInfo: IPlotVariable) =>
        itemInfo?.ShortName === 'Hm0' || itemInfo?.ShortName === 'WS10',
    );

    if (itemsInfos.length === 0) {
      setNotSupported(true);
    } else {
      setVariablesX(itemsInfos);
      if (altimeterComparisonImage === '' && error === '') {
        const indexX = itemsInfos[0];
        setVariableX(indexX);
        getAltimeterComparisonImage(indexX, task.OrderId);
      }
      setNotSupported(false);
    }
  }, [
    coordinates,
    enrichedMetadata,
    activePanel,
    previewDatasetId,
    altimeterComparisonImage,
    error,
  ]);

  function doVariableChange(active: number | string) {
    if (typeof active === 'string') return;

    const item = variablesX.find((a) => a.Index === active)!;
    setVariableX(item);
  }

  const doStartDateChange = (value: Moment | null) => {
    if (!value) return;

    setStartDate(value);
  };

  const doEndDateChange = (value: Moment | null) => {
    if (!value) return;

    setEndDate(value);
  };

  const onUpdateValidation = useCallback(() => {
    if (!enrichedMetadata || !variableX || !previewDatasetId) return;

    const task = enrichedMetadata.find(
      (task: IEnrichedMetadata) =>
        task.DatasetId === previewDatasetId &&
        task.CoordinatesString === props.coordinates.join(),
    );

    if (!task) return;

    getAltimeterComparisonImage(variableX, task.OrderId);
  }, [
    enrichedMetadata,
    variableX,
    previewDatasetId,
    props.coordinates,
    getAltimeterComparisonImage,
  ]);

  const categories = useMemo(() => {
    const labels = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(labels));
  }, [datasets]);

  return (
    <AnalyticsSection
      plotInputContent={
        <AltimeterComparisonForm
          startDate={startDate}
          endDate={endDate}
          previewDatasetId={previewDatasetId}
          previewDatasetTimeRange={previewDatasetTimeRange}
          datasets={datasets}
          categories={categories}
          variablesX={variablesX}
          variableX={variableX}
          notSupported={notSupported}
          datasetChange={datasetChange}
          doVariableChange={doVariableChange}
          doStartDateChange={doStartDateChange}
          doEndDateChange={doEndDateChange}
          isUpdating={isUpdating}
        />
      }
      additionalContent={
        <Button type='link' href={qualityDefinitionLink}>
          {qualityDefinitionLabel}
        </Button>
      }
      isLoading={isUpdating}
      chartContent={
        notSupported ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {limitationMessage}
          </div>
        ) : (
          <>
            <ChartBox
              key={'Altimeter Comparison'}
              title={'Altimeter Comparison'}
              dataset={''}
              height={isUpdating || error.trim() !== '' ? '200px' : 'auto'}
              plot={
                isUpdating ? (
                  <div className={styles.spinner}>
                    <Spin size='large' />
                  </div>
                ) : error.trim() !== '' && !error.includes('Cmems error') ? (
                  <div style={{ padding: '1rem' }}>{error}</div>
                ) : altimeterComparisonImage ? (
                  <img
                    style={{
                      width: '80%',
                      objectFit: 'scale-down',
                      maxHeight: '65vh',
                    }}
                    src={altimeterComparisonImage}
                    alt='AltimeterComparison'
                  />
                ) : (
                  <Empty
                    description={
                      error.includes('Cmems error')
                        ? 'Layer not available'
                        : 'No data available'
                    }
                  />
                )
              }
            />
          </>
        )
      }
      updateContent={
        <Button
          id='Altimeter-Comparison_Update_Button'
          type='primary'
          disabled={notSupported || isUpdating}
          onClick={onUpdateValidation}
          style={{ width: 100 }}
        >
          Update
        </Button>
      }
    />
  );
}
